'use client';

/**
 * AnalyticsCovSurfaceCard — the capability surface as ONE list, four
 * perspectives. Same 178-capability payload (analytics_coverage) re-angled
 * by a perspective toggle; not four cards, one surface. (Canvas card — NOT
 * the left-rail Explorer surface; the toggle is purely in-card view state.)
 *
 *   Map     — grouped by namespace, coloured by usage (where it's alive)
 *   Ranked  — used capabilities by call volume (the workhorses / long tail)
 *   By whom — pivoted by caller role (who exercises what)
 *   Dead    — the unused, rolled into dormant-family chips (the action angle)
 *
 * Every perspective renders its projection COMPLETE — no 3-of-138 scroll
 * peephole. One dataset, multiple encodings (like the productivity chart's
 * series toggle), not a mega-card of unrelated sections.
 *
 * Typography via tokens.ts; strings via response.labels; single useCardExport
 * reflecting the active perspective.
 *
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useMemo, useState } from 'react';
import { LayoutGrid, BarChart3, Timer, Users, Compass, ListX, RefreshCw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { useCardExport } from '@/lib/design/card-export';
import { useCoverageView, fmtInt, fmtMs, fmtAgo, type CoverageCapability } from './use-coverage-view';

type View = 'map' | 'ranked' | 'slowest' | 'bywhom' | 'source' | 'dead';
type L = Record<string, string>;

const RANKED_CAP = 15;

// Sequential indigo ramp + reserved neutral (dataviz: magnitude + zero).
type Bucket = 'unused' | 'cold' | 'warm' | 'hot';
const CELL_CLASS: Record<Bucket, string> = {
  unused: 'bg-muted',
  cold: 'bg-indigo-300 dark:bg-indigo-800',
  warm: 'bg-indigo-500 dark:bg-indigo-600',
  hot: 'bg-indigo-700 dark:bg-indigo-400',
};
function bucketOf(calls: number, max: number): Bucket {
  if (calls <= 0) return 'unused';
  const r = max > 0 ? calls / max : 0;
  if (r >= 0.66) return 'hot';
  if (r >= 0.33) return 'warm';
  return 'cold';
}

export function AnalyticsCovSurfaceCard() {
  const { data, isLoading, queryKey } = useCoverageView();
  const L = data?.labels ?? {};
  const [view, setView] = useState<View>('map');

  const caps = (data?.capabilities ?? []) as CoverageCapability[];
  const maxCalls = useMemo(() => caps.reduce((m, c) => Math.max(m, c.calls), 0), [caps]);
  const roleLabel = (role: string) => (role && role !== '∅' ? role : (L.cov_role_anon ?? 'anonymous'));

  // Dashboard-level refresh: re-run the rollup (neo:write — admin-gated at the
  // gateway), then invalidate the coverage + synopsis reads so every card
  // (KPIs, this surface, the rail narration) repaints from fresh numbers. The
  // freshness pill answers "how stale?" before you decide to click.
  const queryClient = useQueryClient();
  const runStrategy = useCapabilityStore((s) => s.strategyName('analytics', 'rollup'));
  const [refreshing, setRefreshing] = useState(false);
  const updatedLabel = data?.generated_at
    ? `${L.cov_updated ?? 'Updated'} ${fmtAgo(data.generated_at, L)}`
    : '';
  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await api.getStrategyData(runStrategy ?? 'analytics_rollup_run', {});
      await queryClient.invalidateQueries({ queryKey: ['analytics-coverage'] });
      await queryClient.invalidateQueries({ queryKey: ['analytics-synopsis'] });
    } catch {
      /* best-effort — a denied/failed refresh just leaves the stale data + pill */
    } finally {
      setRefreshing(false);
    }
  };

  useCardExport(useMemo(() => {
    const t = data?.totals;
    return {
      label: `${L.cov_explorer_title ?? 'Capabilities'} · ${L[`cov_view_${view}`] ?? view}`,
      text: t ? `${t.used}/${t.capabilities} used (${t.coverage_pct}%) — view=${view}` : (L.loading_message ?? ''),
      json: t ? {
        view,
        coverage: `${t.coverage_pct}${L.unit_pct ?? '%'}`,
        used_of_total: `${t.used} / ${t.capabilities}`,
      } : undefined,
      content_key: 'analytics_cov_surface',
      surface: 'canvas',
      state: { view },
      status: (data ? 'loaded' : 'loading') as 'loading' | 'loaded',
      query_key: queryKey,
    };
  }, [data, view, L, queryKey]));

  const TABS: Array<{ key: View; label: string; Icon: typeof LayoutGrid }> = [
    { key: 'map', label: L.cov_view_map ?? 'Map', Icon: LayoutGrid },
    { key: 'ranked', label: L.cov_view_ranked ?? 'Ranked', Icon: BarChart3 },
    { key: 'slowest', label: L.cov_view_slowest ?? 'Slowest', Icon: Timer },
    { key: 'bywhom', label: L.cov_view_bywhom ?? 'By whom', Icon: Users },
    { key: 'source', label: L.cov_view_source ?? 'By source', Icon: Compass },
    { key: 'dead', label: L.cov_view_dead ?? 'Dead', Icon: ListX },
  ];

  return (
    <div className="bg-card rounded-xl border border-border p-6 flex flex-col h-full">
      {/* Header row 1 — title + freshness/refresh (whole-dataset level) */}
      <div className="flex items-center justify-between gap-3 mb-2">
        <h2 className={text.heading}>{L.cov_explorer_title ?? 'Capabilities'}</h2>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          title={L.cov_refresh ?? 'Refresh data'}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-60 transition-colors shrink-0"
        >
          {updatedLabel && <span className={text.muted}>{updatedLabel}</span>}
          <RefreshCw className={cn('w-3.5 h-3.5', refreshing && 'animate-spin')} />
        </button>
      </div>
      {/* Header row 2 — subtitle + perspective toggle (per-view) */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <p className={text.muted}>{subtitleFor(view, L)}</p>
        <div className="flex items-center gap-1 bg-muted/60 rounded-lg p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setView(t.key)}
              className={cn(
                text.muted,
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-colors',
                view === t.key
                  ? 'bg-card text-primary font-medium shadow-sm'
                  : 'hover:text-foreground',
              )}
            >
              <t.Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body — the active perspective, each a complete projection */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading || !data ? (
          <div className={text.muted}>{L.loading_message ?? 'Loading…'}</div>
        ) : view === 'map' ? (
          <MapView caps={caps} byNamespace={data.by_namespace} maxCalls={maxCalls} L={L} roleLabel={roleLabel} />
        ) : view === 'ranked' ? (
          <RankedView caps={caps} maxCalls={maxCalls} L={L} />
        ) : view === 'slowest' ? (
          <SlowestView caps={caps} L={L} />
        ) : view === 'bywhom' ? (
          <ByWhomView byRole={data.by_role} L={L} roleLabel={roleLabel} />
        ) : view === 'source' ? (
          <SourceView bySource={data.by_source} L={L} />
        ) : (
          <DeadView caps={caps} L={L} />
        )}
      </div>
    </div>
  );
}

function subtitleFor(view: View, L: L): string {
  if (view === 'ranked') return L.cov_ranked_subtitle ?? '';
  if (view === 'slowest') return L.cov_slowest_subtitle ?? '';
  if (view === 'bywhom') return L.cov_role_subtitle ?? '';
  if (view === 'source') return L.cov_source_subtitle ?? '';
  if (view === 'dead') return L.cov_dead_subtitle ?? '';
  return L.cov_map_subtitle ?? '';
}

// ── Map: namespace blocks packed into columns, cells coloured by usage ──
function MapView({ caps, byNamespace, maxCalls, L, roleLabel }: {
  caps: CoverageCapability[];
  byNamespace: { namespace: string }[];
  maxCalls: number;
  L: L;
  roleLabel: (r: string) => string;
}) {
  const groups = useMemo(() => {
    const order = byNamespace.map((n) => n.namespace);
    const byNs = new Map<string, CoverageCapability[]>();
    for (const c of caps) (byNs.get(c.namespace) ?? byNs.set(c.namespace, []).get(c.namespace)!).push(c);
    const seen = new Set<string>();
    const out: Array<{ ns: string; used: number; total: number; items: CoverageCapability[] }> = [];
    for (const ns of order) {
      const items = byNs.get(ns);
      if (!items) continue;
      seen.add(ns);
      out.push({ ns, used: items.filter((i) => i.used).length, total: items.length, items });
    }
    for (const [ns, items] of byNs) if (!seen.has(ns)) out.push({ ns, used: items.filter((i) => i.used).length, total: items.length, items });
    return out;
  }, [caps, byNamespace]);

  return (
    <>
      <Legend L={L} />
      {/* Masonry: namespace blocks flow into columns — the whole surface, dense, no peephole. */}
      <div className="[column-fill:_balance] columns-2 lg:columns-3 gap-x-6 mt-3">
        {groups.map((g) => (
          <div key={g.ns} className="break-inside-avoid mb-4">
            <div className="flex items-baseline gap-2 mb-1.5">
              <span className={text.eyebrow}>{g.ns}</span>
              <span className={cn(text.muted, 'tabular-nums')}>{g.used}/{g.total}</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {g.items.map((c) => (
                <div
                  key={c.strategy}
                  title={`${c.strategy}  ·  ${c.calls} ${L.unit_calls ?? 'calls'}` +
                    (c.roles.length ? `  ·  ${c.roles.map((r) => `${roleLabel(r.role)}:${r.calls}`).join(', ')}` : '') +
                    (c.last_used ? `  ·  ${c.last_used}` : '')}
                  className={cn('w-3.5 h-3.5 rounded-[3px] transition-transform hover:scale-125 hover:ring-2 hover:ring-primary/40',
                    CELL_CLASS[bucketOf(c.calls, maxCalls)])}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Ranked: used capabilities by calls, top N + "+more" (never a scroll dump) ──
function RankedView({ caps, maxCalls, L }: { caps: CoverageCapability[]; maxCalls: number; L: L }) {
  const used = useMemo(() => caps.filter((c) => c.used).sort((a, b) => b.calls - a.calls), [caps]);
  const shown = used.slice(0, RANKED_CAP);
  const more = used.length - shown.length;
  return (
    <div className="flex flex-col gap-2.5">
      {shown.map((c) => (
        <div key={c.strategy}>
          <div className="flex items-baseline justify-between mb-1 gap-3">
            <span className={cn(text.body, 'font-mono truncate')}>{c.strategy}</span>
            <span className={cn(text.muted, 'tabular-nums shrink-0')}>{fmtInt(c.calls)} {L.unit_calls ?? 'calls'}</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-primary" style={{ width: `${maxCalls > 0 ? (100 * c.calls) / maxCalls : 0}%` }} />
          </div>
        </div>
      ))}
      {more > 0 && <p className={cn(text.muted, 'mt-1')}>+{more} {L.cov_ranked_more ?? 'more used'}</p>}
    </div>
  );
}

// ── Slowest: used capabilities by p99 latency, severity-tinted (SLO angle) ──
function SlowestView({ caps, L }: { caps: CoverageCapability[]; L: L }) {
  const used = useMemo(
    () => caps.filter((c) => c.used && c.p99_ms > 0).sort((a, b) => b.p99_ms - a.p99_ms),
    [caps],
  );
  const shown = used.slice(0, RANKED_CAP);
  const more = used.length - shown.length;
  const maxP99 = used.length ? used[0].p99_ms : 0;
  const barClass = (ms: number) => (ms >= 5000 ? 'bg-red-500' : ms >= 1000 ? 'bg-amber-500' : 'bg-primary');
  if (used.length === 0) return <div className={text.muted}>{L.cov_window_all ?? 'no usage in window'}</div>;
  return (
    <div className="flex flex-col gap-2.5">
      {shown.map((c) => (
        <div key={c.strategy}>
          <div className="flex items-baseline justify-between mb-1 gap-3">
            <span className={cn(text.body, 'font-mono truncate')}>{c.strategy}</span>
            <span className={cn(text.muted, 'tabular-nums shrink-0')}>
              {fmtMs(c.p99_ms, L.unit_ms ?? 'ms', L.unit_s ?? 's')}
            </span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className={cn('h-full rounded-full', barClass(c.p99_ms))}
                 style={{ width: `${maxP99 > 0 ? (100 * c.p99_ms) / maxP99 : 0}%` }} />
          </div>
        </div>
      ))}
      {more > 0 && <p className={cn(text.muted, 'mt-1')}>+{more} {L.cov_ranked_more ?? 'more used'}</p>}
    </div>
  );
}

// ── By whom: role summary bars (short by nature) ──
function ByWhomView({ byRole, L, roleLabel }: {
  byRole: { role: string; capabilities_used: number; calls: number }[];
  L: L;
  roleLabel: (r: string) => string;
}) {
  const maxCalls = byRole.reduce((m, r) => Math.max(m, r.calls), 0);
  if (byRole.length === 0) return <div className={text.muted}>{L.cov_window_all ?? 'no usage in window'}</div>;
  return (
    <div className="flex flex-col gap-3.5">
      {byRole.map((r) => (
        <div key={r.role}>
          <div className="flex items-baseline justify-between mb-1">
            <span className={cn(text.title, 'capitalize')}>{roleLabel(r.role)}</span>
            <span className={cn(text.muted, 'tabular-nums')}>
              {fmtInt(r.capabilities_used)} · {fmtInt(r.calls)} {L.unit_calls ?? 'calls'}
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-primary" style={{ width: `${maxCalls > 0 ? (100 * r.calls) / maxCalls : 0}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── By source: tagged web traffic by referral source (the acquisition lens) ──
function SourceView({ bySource, L }: {
  bySource: { utm_source: string; calls: number }[];
  L: L;
}) {
  const maxCalls = bySource.reduce((m, r) => Math.max(m, r.calls), 0);
  if (bySource.length === 0) {
    return <div className={text.muted}>{L.cov_source_empty ?? 'No tagged referral traffic in the window'}</div>;
  }
  return (
    <div className="flex flex-col gap-3.5">
      {bySource.map((r) => (
        <div key={r.utm_source}>
          <div className="flex items-baseline justify-between mb-1">
            <span className={cn(text.title, 'capitalize')}>{r.utm_source}</span>
            <span className={cn(text.muted, 'tabular-nums')}>{fmtInt(r.calls)} {L.unit_calls ?? 'calls'}</span>
          </div>
          <div className="h-2.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-primary transition-all"
                 style={{ width: `${maxCalls > 0 ? (100 * r.calls) / maxCalls : 0}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Dead: the unused, rolled into dormant-family chips (no 138-row scroll) ──
function DeadView({ caps, L }: { caps: CoverageCapability[]; L: L }) {
  const families = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of caps) if (!c.used) counts.set(c.namespace, (counts.get(c.namespace) ?? 0) + 1);
    return Array.from(counts, ([ns, n]) => ({ ns, n })).sort((a, b) => b.n - a.n);
  }, [caps]);
  if (families.length === 0) return <div className={text.muted}>{L.cov_unused_empty ?? 'Every capability was used.'}</div>;
  return (
    <div className="flex flex-wrap gap-2">
      {families.map((f) => (
        <div key={f.ns} className="flex items-center gap-1.5 bg-muted/70 rounded-md px-2.5 py-1.5">
          <span className={cn(text.body)}>{f.ns}</span>
          <span className={cn(text.muted, 'tabular-nums font-medium')}>{f.n}</span>
        </div>
      ))}
    </div>
  );
}

function Legend({ L }: { L: L }) {
  const items: Array<[Bucket, string]> = [
    ['unused', L.cov_map_legend_unused ?? 'Unused'],
    ['cold', L.cov_map_legend_cold ?? 'Cold'],
    ['warm', L.cov_map_legend_warm ?? 'Warm'],
    ['hot', L.cov_map_legend_hot ?? 'Hot'],
  ];
  return (
    <div className="flex items-center gap-3">
      {items.map(([b, label]) => (
        <div key={b} className="flex items-center gap-1.5">
          <span className={cn('w-3 h-3 rounded-[3px]', CELL_CLASS[b])} />
          <span className={text.muted}>{label}</span>
        </div>
      ))}
    </div>
  );
}
